/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectVisualFilters,
  selectPmsSync,
  selectTargetDate,
  selectPaceHorizon,
  selectHotelName,
  selectEnableHotelGroups,
  selectReportType,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
  selectResetBookingDate,
  selectPartialHours,
  selectLastPartialBookingDate,
} from 'modules/dashboard/selectors';
import { PACE_HORIZON_SELECTION, REPORT_TYPE, TAGS } from 'modules/dashboard/constants';
import {
  getBookingDateSchema,
  getComparisonBookingDateSchema,
  getComparisonStayDateSchema,
  getDashboardPmsSyncSchema,
  getHotelIdSchema,
  getMaxStayDateSchema,
  getPaceWidgetPaceHorizonSchema,
  getPmsSyncSchema,
  getSegmentFocusOnSchema,
  getTargetDateSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import {
  selectPaceBookingPaceWindow,
  selectPaceBookingDate,
  selectPaceCancellationFilterList,
  selectPaceFocusOn,
  selectPaceChartBreakdown,
  selectIsPaceDetailed,
  selectIsFocusOnSet,
  selectPaceAlignmentToggle,
  selectPaceSymmetricComparisonToggle,
  selectPaceDateComparison,
} from 'modules/dashboard/components/tab-container/pace-tabs/selectors';
import { paceActions } from 'modules/dashboard/components/tab-container/pace-tabs/slice';
import writeLog from 'modules/common/utils/filter-log';
import { getSelectedBreakdown } from '../../../../functions';
import calculateComparisonDates from '../../../../../common/utils/dateComparisonUtils';

/**
 * Custom hook to generate pace widget cancellation filter schemas
 */
const usePaceCancellationWidget = () => {
  const dispatch = useDispatch();
  //
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const bookingDate = useSelector(selectPaceBookingDate);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const reportType = useSelector(selectReportType);
  const cancellationFilterList = useSelector(selectPaceCancellationFilterList);
  const bookingPaceWindow = useSelector(selectPaceBookingPaceWindow);
  const focusOn = useSelector(selectPaceFocusOn);
  const chartBreakdown = useSelector(selectPaceChartBreakdown);
  const isDetailed = useSelector(selectIsPaceDetailed);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const alignmentToggle = useSelector(selectPaceAlignmentToggle);
  const dateComparison = useSelector(selectPaceDateComparison);
  const symmetricComparisonToggle = useSelector(selectPaceSymmetricComparisonToggle);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const partialHours = useSelector(selectPartialHours);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);
  //
  useEffect(() => {
    if (!IsFocusOnSet || reportType !== REPORT_TYPE.PACE || !hotelId || !visualFilters) return;
    const targetDateFilter = getTargetDateSchema(
      visualFilters,
      targetDate?.startDate,
      targetDate?.endDate
    );
    //
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);
    let hotelListIds = [];
    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    let hotelIdFilter = [];
    if (enableHotelGroups) {
      hotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    }
    const paceHorizonFilter = getPaceWidgetPaceHorizonSchema(
      visualFilters,
      paceHorizon,
      bookingPaceWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_HORIZON
    );
    const pmsSyncFilter = getPmsSyncSchema(visualFilters, pmsSyncToggle, TAGS.PACE_WIDGET);
    const selectedBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    const segmentFocusOnFilter = getSegmentFocusOnSchema(
      selectedBreakdown,
      focusOn?.map((item) => item?.label)
    );
    const pmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    const { adjustedBookingDate, adjustedStayDate } = calculateComparisonDates(
      targetDate,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      bookingDate, // not used in Pace
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    const comparisonBookingDateFilter = getComparisonBookingDateSchema(
      visualFilters,
      adjustedBookingDate?.startDate,
      adjustedBookingDate?.endDate
    );
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, targetDate?.endDate);
    //
    if (
      targetDateFilter &&
      hotelIdFilter &&
      paceHorizonFilter &&
      pmsSyncFilter &&
      bookingDateFilter &&
      segmentFocusOnFilter
    ) {
      const filters = [
        targetDateFilter,
        hotelIdFilter,
        paceHorizonFilter,
        ...pmsSyncFilter,
        bookingDateFilter,
        ...segmentFocusOnFilter,
        ...pmsSyncSchema,
        maxStayDateFilter,
        comparisonStayDateFilter,
        comparisonBookingDateFilter,
      ];
      //
      if (JSON.stringify(cancellationFilterList) !== JSON.stringify(filters)) {
        //
        writeLog('Pace Cancellation Count Filters', filters);
        dispatch(paceActions.setPaceCancellationFilterList(filters));
      }
    }
  }, [
    pmsSyncToggle,
    visualFilters,
    targetDate,
    hotelId,
    paceHorizon,
    enableHotelGroups,
    reportType,
    bookingDate,
    bookingPaceWindow,
    focusOn,
    chartBreakdown,
    isDetailed,
    IsFocusOnSet,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    resetBookingDate,
  ]);
};
//
export default usePaceCancellationWidget;
